import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { CampaignChannelBadges, CampaignStatusPill } from '#/entities/campaign'
import type { CampaignDetailHeader } from '#/entities/campaign'
import { Button } from '#/shared/ui/button'
import { ChevronLeftIcon } from '#/shared/ui/icons'
import {
  SEGMENTED_TAB_VARIANTS,
  SegmentedTabs,
} from '#/shared/ui/segmented-tabs'

import type { CampaignDetailTab } from '../model/detail-tabs'
import { campaignLifecycleAction } from '../model/use-campaign-actions'
import type { UseCampaignActionsResult } from '../model/use-campaign-actions'
import type { CampaignDetailTabItem } from '../model/use-campaign-detail'
import { CampaignPowerDialButton } from './campaign-power-dial-button'

interface CampaignDetailHeaderProps {
  header: CampaignDetailHeader
  tabs: CampaignDetailTabItem[]
  activeTab: CampaignDetailTab
  onTabChange: (tab: CampaignDetailTab) => void
  actions: UseCampaignActionsResult
  onAddStep: () => void
}

interface HeaderActionsProps {
  header: CampaignDetailHeader
  actions: UseCampaignActionsResult
  onAddStep: () => void
}

function HeaderActions({ header, actions, onAddStep }: HeaderActionsProps) {
  const { t } = useTranslation()
  const lifecycle = campaignLifecycleAction(header.status)
  return (
    <div className="flex flex-wrap items-center gap-2">
      {header.canPowerDial && <CampaignPowerDialButton header={header} />}
      <Button
        variant="outline"
        size="xs"
        className="px-3.5"
        disabled={actions.isBusy}
        onClick={actions.onDuplicate}
      >
        {t('campaigns.detail.actions.duplicate')}
      </Button>
      {lifecycle != null && (
        <Button
          variant="outline"
          size="xs"
          className="px-3.5"
          disabled={actions.isBusy}
          onClick={() => actions.onLifecycle(lifecycle)}
        >
          {t(`campaigns.detail.actions.${lifecycle}`)}
        </Button>
      )}
      <Button
        size="xs"
        pressEffect
        className="px-3.5 font-medium"
        onClick={onAddStep}
      >
        {t('campaigns.detail.actions.addStep')}
      </Button>
    </div>
  )
}

export function CampaignDetailHeaderCard({
  header,
  tabs,
  activeTab,
  onTabChange,
  actions,
  onAddStep,
}: CampaignDetailHeaderProps) {
  const { t } = useTranslation()
  const items = tabs.map((tab) => ({
    value: tab.key,
    render: () => (
      <>
        {tab.label}
        {tab.count != null && (
          <span className="text-xs text-gray-300">{tab.count}</span>
        )}
      </>
    ),
  }))

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/campaign"
        className="flex w-fit items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900"
      >
        <ChevronLeftIcon className="size-3" />
        {t('campaigns.detail.backToList')}
      </Link>
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4 px-4 py-3.5">
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">
                {header.name}
              </h1>
              <CampaignChannelBadges channels={header.channels} />
              <CampaignStatusPill status={header.status} />
            </div>
            <p className="text-xs text-gray-500">{header.summary}</p>
          </div>
          <HeaderActions
            header={header}
            actions={actions}
            onAddStep={onAddStep}
          />
        </div>
        <div className="px-4 pb-3">
          <SegmentedTabs
            items={items}
            value={activeTab}
            onValueChange={onTabChange}
            variant={SEGMENTED_TAB_VARIANTS.SOFT}
            className="w-max"
          />
        </div>
      </section>
    </div>
  )
}
